// The decisions job polling makes, kept out of SpatialVision.js so `node --test`
// can reach them: that module pulls in the API client and the DOM on import.

// A photo job finishes on the worker in about 1.2 s, so a status only moves in the
// first seconds. Polling asks the server for every active job on every tick, so
// five queued jobs cost 25 requests a second while the window is at 200 ms. That
// is the price of catching the first result, and it lasts two seconds.
export const nextJobPollDelayMs = elapsedMs => {
  if (elapsedMs < 2000) return 200; // covers one 1.2 s photo job end to end
  if (elapsedMs < 10000) return 500; // past a photo job: a video, or a queue
  return 1000;
};

// A job the previous tick did not carry is work the operator just started, so the
// fast window is measured from it. A job leaving the list restarts nothing: the
// ones that stay are as old as they were.
export const hasNewActiveJob = (previousIds, activeIds) =>
  activeIds.some(id => !previousIds.includes(id));

// Compares the merged list against the one already on screen, by name and not by
// identity: every set deep-clones the state, and the list endpoint writes the keys
// of a job in a different order than the single-job endpoint. A poll that brings
// the same jobs back must not patch, because patch() replaces the whole state and
// re-renders every subscriber, toolbar <select> elements included.
export const sameJobs = (previous, next) => {
  if (previous === next) return true;
  // A bbox reads Number(detection.coordX || 0), so a non-numeric coordinate stores
  // NaN, and NaN !== NaN would report a change on every tick from then on.
  if (Number.isNaN(previous) && Number.isNaN(next)) return true;
  if (Array.isArray(previous) || Array.isArray(next)) {
    return (
      Array.isArray(previous) &&
      Array.isArray(next) &&
      previous.length === next.length &&
      previous.every((job, index) => sameJobs(job, next[index]))
    );
  }
  if (!previous || !next) return false;
  if (typeof previous !== 'object' || typeof next !== 'object') return false;
  const keys = Object.keys(previous);
  return (
    keys.length === Object.keys(next).length &&
    keys.every(key => key in next && sameJobs(previous[key], next[key]))
  );
};

// Which job and which frame the operator is looking at is theirs; a poll reports
// the server's progress and nothing else. The selection comes back untouched while
// it still resolves, and moves only once the job or the frame it names has left the
// list. A move puts a different image on the canvas, so the zoom and pan go back
// to the default with it. A selection that is kept leaves the canvas view out of
// the patch, and so does a frame the operator never named: the canvas was already
// showing imageResults[0] through the fallback, so filling the id in moves nothing.
export const repairJobSelection = (
  jobs,
  { activeJobId, selectedJobItemId },
) => {
  const activeJob =
    jobs.find(job => String(job.id) === String(activeJobId)) || jobs[0] || null;
  const items = activeJob?.imageResults || [];
  const keptItem = items.find(
    item => String(item.id) === String(selectedJobItemId),
  );
  const selection = {
    activeJobId: activeJob?.id ?? null,
    selectedJobItemId: keptItem?.id ?? items[0]?.id ?? null,
  };
  const namedFrame =
    selectedJobItemId !== null && selectedJobItemId !== undefined;
  if (!namedFrame || keptItem) return selection;
  return { ...selection, canvasZoom: 100, canvasPanX: 0, canvasPanY: 0 };
};

// Ids reach the state from two endpoints and from the operator, so one side holding
// 7 and the other '7' says nothing about a selection having moved.
const sameChoice = (a, b) =>
  a === null || a === undefined || b === null || b === undefined
    ? (a ?? null) === (b ?? null)
    : String(a) === String(b);

// What a poll reply patches, or null for one that changes nothing on screen.
// `stale` marks a reply that is no longer the newest one asked for: the page runs its
// own poll loop and the AI Analyzer drawer runs another over the same endpoint, so
// two requests can be in the air at once, and mergeJobs writes incoming over existing
// by id with no regard for which snapshot is older. Applying the late reply of the
// earlier request puts a finished job back into running and takes its detections off
// the canvas. runSettingsSaveVersion drops a superseded save the same way.
// The repair of the selection runs on every reply, not only on a changed list:
// nothing else in the page runs it, and a selection naming a job the list does not
// hold leaves the canvas on jobs[0] through its fallback while the strip highlights
// nothing at all.
export const jobsPollPatch = (previousJobs, jobs, selection, stale = false) => {
  if (stale) return null;
  const repaired = repairJobSelection(jobs, selection);
  const moved = Object.entries(repaired).some(
    ([key, value]) => !sameChoice(selection[key], value),
  );
  if (!moved && sameJobs(previousJobs, jobs)) return null;
  return repaired;
};
